import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { X } from "@phosphor-icons/react";

interface DeleteDocumentDialogProps {
  open: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Confirmation dialog for deleting a generated document. A reason is
 * required — it's persisted on the CANCELLED verification record for audit.
 */
const DeleteDocumentDialog: React.FC<DeleteDocumentDialogProps> = ({
  open,
  isSubmitting = false,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Delete Document
        <IconButton
          aria-label="Close dialog"
          size="small"
          onClick={onClose}
          disabled={isSubmitting}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body.small" color="text.secondary" sx={{ mb: 4 }}>
          This will delete the document and remove any pending signing requests. This action
          cannot be undone.
        </Typography>
        <TextField
          label="Reason for deleting"
          fullWidth
          multiline
          minRows={3}
          required
          autoFocus
          disabled={isSubmitting}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={onClose} disabled={isSubmitting}>
          Go Back
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          loading={isSubmitting}
          disabled={!reason.trim()}
          onClick={() => onConfirm(reason.trim())}
        >
          Delete Document
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteDocumentDialog;
